/**
 * ItemClassificationBadge
 *
 * Compact inline badge showing item category and altitude.
 * Use this wherever an item's classification needs to be visible
 * in lists, proposals, sessions, or the Plan of Record.
 *
 * Backed by the canonical CATEGORY_BADGE color system in itemCategoryColors.ts.
 */

import { ArrowUpCircle, Bot, User, GitMerge } from 'lucide-react';
import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../lib/itemCategoryColors';
import type { ExecutionProfile } from '../../types/prioritization/itemClassification';

// eslint-disable-next-line react-refresh/only-export-components
export const CATEGORY_LABELS: Record<string, string> = {
  problem: 'Problem',
  opportunity: 'Opportunity',
  risk: 'Risk',
  solution: 'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

const CONFIDENCE_CLASSES: Record<string, string> = {
  high: 'bg-[var(--green-dim)] text-[var(--green)] border-[var(--green)] dark:bg-[var(--green)]/25 dark:text-[var(--text-ter)] dark:border-[var(--green)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] dark:bg-[var(--mustard)]/25 dark:text-[var(--mustard)] dark:border-[var(--mustard)]',
  low: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)] dark:bg-[var(--coral)]/25 dark:text-[var(--coral)] dark:border-[var(--coral)]',
};

export function CategoryChip({ category, className = '' }: { category: string; className?: string }) {
  const badge = CATEGORY_BADGE[category] ?? CATEGORY_BADGE_FALLBACK;
  const label = CATEGORY_LABELS[category] ?? (category.charAt(0).toUpperCase() + category.slice(1));
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badge.bg} ${badge.text} ${badge.border} ${className}`}
    >
      {label}
    </span>
  );
}

export function AltitudeChip({ level, category, className = '' }: { level: number; category?: string | null; className?: string }) {
  const altitude = useAltitudeText();
  const label = level >= 1 && level <= 5 ? altitude.tag(level, category) : undefined;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)] dark:bg-[var(--bg-raised)]/40 ${className}`}
    >
      <ArrowUpCircle className="h-3 w-3 shrink-0" />
      {`Altitude ${level}`}
      {label && <span className="opacity-60">· {label}</span>}
    </span>
  );
}

export function ConfidenceChip({ confidence, className = '' }: { confidence: 'high' | 'medium' | 'low'; className?: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${CONFIDENCE_CLASSES[confidence]} ${className}`}
    >
      {confidence.charAt(0).toUpperCase() + confidence.slice(1)} confidence
    </span>
  );
}

const EXECUTION_PROFILE_CONFIG: Record<ExecutionProfile, { label: string; icon: typeof Bot; classes: string }> = {
  'human-led':     { label: 'Human-led',     icon: User,     classes: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/25 dark:border-[var(--slate)]' },
  'ai-assisted':   { label: 'AI-assisted',   icon: Bot,      classes: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/25 dark:border-[var(--slate)]' },
  'ai-executable': { label: 'AI-executable', icon: Bot,      classes: 'bg-[var(--green-dim)] text-[var(--green)] border-[var(--green)] dark:bg-[var(--green)]/25 dark:text-[var(--text-ter)] dark:border-[var(--green)]' },
  'hybrid':        { label: 'Hybrid',        icon: GitMerge, classes: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] dark:bg-[var(--mustard)]/25 dark:text-[var(--mustard)] dark:border-[var(--mustard)]' },
};

export function ExecutionProfileChip({ profile, className = '' }: { profile: ExecutionProfile; className?: string }) {
  const config = EXECUTION_PROFILE_CONFIG[profile];
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.classes} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {config.label}
    </span>
  );
}

interface ItemClassificationBadgeProps {
  category?: string | null;
  altitude?: number | string | null;
  executionProfile?: ExecutionProfile | null;
  confidence?: 'high' | 'medium' | 'low' | null;
  /** Show only category chip (omit altitude and execution profile) */
  categoryOnly?: boolean;
  className?: string;
}

/**
 * Combined inline badge. Renders category + altitude + execution profile (and optionally confidence).
 * Renders nothing if both category and altitude are absent.
 */
export function ItemClassificationBadge({
  category,
  altitude,
  executionProfile,
  confidence,
  categoryOnly = false,
  className = '',
}: ItemClassificationBadgeProps) {
  const altitudeNum = altitude != null ? Number(altitude) : null;

  if (!category && !altitudeNum) return null;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      {category && <CategoryChip category={category} />}
      {!categoryOnly && altitudeNum != null && <AltitudeChip level={altitudeNum} category={category} />}
      {!categoryOnly && executionProfile && <ExecutionProfileChip profile={executionProfile} />}
      {confidence && <ConfidenceChip confidence={confidence} />}
    </span>
  );
}
